import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import {
    GridColumn,
    GridColumnMenuFilter,
    GridColumnProps,
    GridHeaderCellProps,
    GridColumnMenuProps,
    GridRowProps,
    GridCustomRowProps,
} from '@progress/kendo-react-grid';
import cn from 'clsx';
import React from 'react';
import GridHeaderCell from '@components/core/expandable/grid-v2/grid-header-cell';
import { setChildWidthV2, setWidthV2 } from '@components/core/expandable/grid-v2/grid-v2.service';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnMenuBooleanFilterV2 } from '@components/core/grid-filters-v2';
import { RiskStatusType } from 'types/graphql-global-types';
import { GRID_ROW_TEST_ID_MASTER } from '@utils/automation-test-helpers';

const getColumnMenu = (column: any) =>
    column.notFilterable
        ? null
        : (props: GridColumnMenuProps) => (
              <div data-testid="grid-filter-menu">
                  <GridColumnMenuFilter
                      {...props}
                      expanded
                      filterUI={
                          column.filterUi ||
                          (props.column.filter === 'boolean' ? GridColumnMenuBooleanFilterV2 : undefined)
                      }
                  />
              </div>
          );

export const getGridMinWidth = (columns: IExpandableGridColumnV2[], extraColumnWidth?: number): number =>
    columns.reduce((sum, col) => {
        if (col.show === false) return sum;
        if (col.children?.length) return sum + Math.max(getGridMinWidth(col.children), col.minWidth || 0);
        return sum + (col.minWidth || 0);
    }, 0) + (extraColumnWidth ?? 0);

export const renderGridV2Columns = (
    filter: CompositeFilterDescriptor,
    gridElement: HTMLDivElement,
    columns: IExpandableGridColumnV2[],
    extraColumnWidth: number,
    minGridWidth: number,
) => {
    const shouldGridExpand = gridElement?.offsetWidth > minGridWidth;

    return columns.map((column) => {
        const getVisibleChildren = (children: IExpandableGridColumnV2[]): IExpandableGridColumnV2[] =>
            children.filter((col) => col.show !== false);
        const childrenSumWidth = (children: IExpandableGridColumnV2[]) =>
            children
                ? getVisibleChildren(children).reduce((sum, item) => sum + (item.minWidth || item.width || 0), 0)
                : 0;

        let { width } = column;

        if (column.minWidth) {
            const minWidthOfColOrChildren = Math.max(column.minWidth, childrenSumWidth(column?.children ?? []));
            width = setWidthV2(
                minWidthOfColOrChildren,
                column.field,
                gridElement,
                columns,
                shouldGridExpand,
                minGridWidth,
            );
        }

        const gridColumnProps = (col: IExpandableGridColumnV2, item?: IExpandableGridColumnV2): GridColumnProps => ({
            minWidth: col.locked ? item?.minWidth || col.minWidth : 5,
            resizable: !item?.locked || !column.locked,
            sortable: !item?.notSortable || !column.notSortable,
            filterable: !item?.notFilterable || !column.notFilterable,
        });
        return column.show ? (
            <GridColumn
                {...column}
                {...gridColumnProps(column)}
                key={column.field}
                title={column.title ?? column.label ?? column.field}
                width={width}
                cells={{
                    ...column.cells,
                    headerCell: (props: GridHeaderCellProps) =>
                        GridHeaderCell({
                            cellProps: props,
                            column,
                            filter,
                            columnMenu: getColumnMenu(column),
                        }),
                }}
            >
                {column.children && column.children.length
                    ? column.children
                          .filter((el: any) => el.show !== false)
                          .map((child: IExpandableGridColumnV2) => {
                              const childWidth = setChildWidthV2(
                                  child.minWidth,
                                  getVisibleChildren(column.children).length,
                                  width,
                                  childrenSumWidth(column.children),
                                  !shouldGridExpand,
                              );
                              return (
                                  <GridColumn
                                      {...gridColumnProps(column, child)}
                                      width={childWidth}
                                      key={child.field}
                                      {...child}
                                      cells={{
                                          ...child.cells,
                                          headerCell: (props: GridHeaderCellProps) =>
                                              GridHeaderCell({
                                                  cellProps: props,
                                                  column: child,
                                                  filter,
                                                  columnMenu: getColumnMenu(child),
                                              }),
                                      }}
                                  >
                                      {child.children && child.children.length
                                          ? child.children
                                                .filter((el: any) => el.show !== false)
                                                .map((grandchild: IExpandableGridColumnV2) => {
                                                    const grandChildWidth = setChildWidthV2(
                                                        grandchild.minWidth,
                                                        getVisibleChildren(child.children).length,
                                                        childWidth,
                                                        childrenSumWidth(child.children),
                                                        !shouldGridExpand,
                                                    );
                                                    return (
                                                        <GridColumn
                                                            {...gridColumnProps(column, grandchild)}
                                                            key={grandchild.field}
                                                            width={grandChildWidth}
                                                            {...grandchild}
                                                            cells={{
                                                                ...grandchild.cells,
                                                                headerCell: (props: GridHeaderCellProps) =>
                                                                    GridHeaderCell({
                                                                        cellProps: props,
                                                                        column: grandchild,
                                                                        filter,
                                                                        columnMenu: getColumnMenu(grandchild),
                                                                    }),
                                                            }}
                                                        >
                                                            {grandchild.children && grandchild.children.length
                                                                ? grandchild.children
                                                                      .filter((el: any) => el.show !== false)
                                                                      .map((leaf: any) => (
                                                                          <GridColumn
                                                                              {...gridColumnProps(column, leaf)}
                                                                              key={leaf.field}
                                                                              width={
                                                                                  leaf.minWidth
                                                                                      ? setChildWidthV2(
                                                                                            leaf.minWidth,
                                                                                            getVisibleChildren(
                                                                                                grandchild.children,
                                                                                            ).length,
                                                                                            grandChildWidth,
                                                                                            childrenSumWidth(
                                                                                                grandchild.children,
                                                                                            ),
                                                                                            !shouldGridExpand,
                                                                                        )
                                                                                      : leaf.minWidth || leaf.width
                                                                              }
                                                                              {...leaf}
                                                                              cells={{
                                                                                  ...leaf.cells,
                                                                                  headerCell: (
                                                                                      props: GridHeaderCellProps,
                                                                                  ) =>
                                                                                      GridHeaderCell({
                                                                                          cellProps: props,
                                                                                          column: leaf,
                                                                                          filter,
                                                                                          columnMenu:
                                                                                              getColumnMenu(leaf),
                                                                                      }),
                                                                              }}
                                                                          />
                                                                      ))
                                                                : undefined}
                                                        </GridColumn>
                                                    );
                                                })
                                          : undefined}
                                  </GridColumn>
                              );
                          })
                    : undefined}
            </GridColumn>
        ) : null;
    });
};
export const gridV2RowRender = (props: GridCustomRowProps) => {
    const { trProps, children } = props;
    const isRowDeleted = props.dataItem.deleted;
    const isNotApplicable = props.dataItem.status === RiskStatusType.NOT_APPLICABLE;
    const trCustomProps: Record<string, string | number> = {
        className: cn(trProps?.className, {
            'k-master-row--deleted': isRowDeleted,
            'k-master-row--not-applicable': isNotApplicable,
            'k-alt': props?.isAltRow,
        }),
        'data-testid': GRID_ROW_TEST_ID_MASTER,
    };

    return (
        <tr {...trProps} {...trCustomProps}>
            {children}
        </tr>
    );
};
